// saenggibuApi.js - My-Saenggibu API 서비스

import { auth } from '../firebase/config';
import { getApiUrl } from '../utils/apiConfig';
import { fetchWithRetry, calcApiTimeout } from '../utils/fetchWithRetry';
import { readSseJson } from '../utils/sseJsonStream';

// 인증 헤더 생성
const getAuthHeaders = async () => {
  const user = auth.currentUser;
  
  if (!user) {
    return {
      'Content-Type': 'application/json',
    };
  }

  try {
    const token = await user.getIdToken();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'X-User-ID': user.uid,
    };
  } catch (error) {
    console.error('토큰 가져오기 실패:', error);
    return {
      'Content-Type': 'application/json',
      'X-User-ID': user.uid,
    };
  }
};

const throwApiError = (response, errorData) => {
  const detail = errorData?.detail;
  const message = typeof detail === 'string' && detail
    ? detail
    : `HTTP error! status: ${response.status}`;
  const error = new Error(message);
  error.status = response.status;
  error.detail = detail;
  error.error = errorData?.error || null;
  error.warnings = errorData?.warnings;
  error.body = errorData;
  throw error;
};

// API 요청 헬퍼 함수
const apiRequest = async (url, options = {}) => {
  try {
    const authHeaders = await getAuthHeaders();
    const response = await fetch(getApiUrl(url), {
      ...options,
      headers: {
        ...authHeaders,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throwApiError(response, errorData);
    }

    return await response.json();
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

const apiRequestWithRetry = async (url, options = {}, retryOptions = {}) => {
  try {
    const authHeaders = await getAuthHeaders();
    const response = await fetchWithRetry(
      getApiUrl(url),
      {
        ...options,
        headers: {
          ...authHeaders,
          ...options.headers,
        },
      },
      retryOptions
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throwApiError(response, errorData);
    }

    return await response.json();
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

// ========================================
// 생기부 히스토리 관련 API
// ========================================

// 생기부 생성 기록 조회
export const getSaenggibuHistory = async (limit = 10, offset = 0) => {
  return await apiRequest(`/api/saenggibu/history?limit=${limit}&offset=${offset}`);
};

// 생기부 생성 기록 상세 조회
export const getSaenggibuRecord = async (recordId) => {
  return await apiRequest(`/api/saenggibu/history/${recordId}`);
};

// 근거 보기: 결과 문장마다 교사가 넣은 내용 중 어디를 썼는지 (없으면 서버가 그 자리에서 계산)
export const getSaenggibuEvidence = async (recordId, keys = []) => {
  const query = keys.length ? `?keys=${encodeURIComponent(keys.join(','))}` : '';
  return await apiRequestWithRetry(
    `/api/saenggibu/history/${recordId}/evidence${query}`,
    {},
    { maxRetries: 0, timeoutMs: 120_000 },
  );
};

// 생기부 생성 기록 이름 수정
export const updateSaenggibuRecordMeta = async (recordId, payload) => {
  return await apiRequest(`/api/saenggibu/history/${recordId}/meta`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
};

// 엑셀 다운로드용 생기부 생성 기록 상세 일괄 조회
export const getSaenggibuHistoryExportData = async (recordIds) => {
  return await apiRequest('/api/saenggibu/history/export-data', {
    method: 'POST',
    body: JSON.stringify({ record_ids: recordIds }),
  });
};

// 생기부 생성 기록 삭제
export const deleteSaenggibuRecord = async (recordId) => {
  return await apiRequest(`/api/saenggibu/history/${recordId}`, {
    method: 'DELETE'
  });
};

// 생기부 생성 기록의 특정 버전 삭제
export const deleteSaenggibuVersion = async (recordId, versionNumber) => {
  return await apiRequest(`/api/saenggibu/history/${recordId}/version/${versionNumber}`, {
    method: 'DELETE'
  });
};

// 생기부 생성 기록의 특정 버전 내용 수정
export const updateSaenggibuVersion = async (recordId, versionNumber, content, revision = null) => {
  return await apiRequest(`/api/saenggibu/history/${recordId}/version/${versionNumber}`, {
    method: 'PATCH',
    body: JSON.stringify({ content, revision })
  });
};

// 생기부 생성 기록의 특정 버전 이름 수정
export const updateSaenggibuVersionLabel = async (recordId, versionNumber, customLabel) => {
  return await apiRequest(`/api/saenggibu/history/${recordId}/version/${versionNumber}/label`, {
    method: 'PATCH',
    body: JSON.stringify({ custom_label: customLabel })
  });
};

// 다반 교과세특 결과를 하나의 기록으로 저장
export const saveMultiClassSaenggibuRecord = async (payload) => {
  return await apiRequest('/api/saenggibu/history/multiclass', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
};

// 일괄 생성 기록의 학생별 생기부 내용 수정
export const updateBatchSaenggibuResult = async (recordId, studentIndex, content, revision = null) => {
  return await apiRequest(`/api/saenggibu/history/${recordId}/batch-result/${studentIndex}`, {
    method: 'PATCH',
    body: JSON.stringify({ content, revision })
  });
};

// 일괄 생성 기록의 학생 이름 수정
export const updateBatchSaenggibuStudentName = async (recordId, studentIndex, studentName) => {
  return await apiRequest(`/api/saenggibu/history/${recordId}/batch-result/${studentIndex}/student-name`, {
    method: 'PATCH',
    body: JSON.stringify({ student_name: studentName })
  });
};

// 다반 교과세특 기록의 특정 반/학생 내용 수정
export const updateMultiClassSaenggibuVersion = async (recordId, classIndex, versionIndex, content, revision = null) => {
  return await apiRequest(`/api/saenggibu/history/${recordId}/multi-class/${classIndex}/version/${versionIndex}`, {
    method: 'PATCH',
    body: JSON.stringify({ content, revision })
  });
};

// 생성된 생기부 문장 AI 다듬기
export const refineSaenggibuContent = async (payload) => {
  return await apiRequestWithRetry('/api/saenggibu/refine', {
    method: 'POST',
    body: JSON.stringify(payload)
  }, {
    maxRetries: 1,
    timeoutMs: calcApiTimeout('saenggibu-refine')
  });
};

/** 반 점검에서 겹친 문장 종류(activity|generic|individual). 실패하면 빈 객체. */
export const classifyOverlapKinds = async ({ category = '', sentences = [] }) => {
  if (!sentences.length) return {};
  try {
    const data = await apiRequestWithRetry('/api/saenggibu/overlap-kinds', {
      method: 'POST',
      body: JSON.stringify({ category, sentences: sentences.slice(0, 40) }),
    }, { maxRetries: 0, timeoutMs: 35_000 });
    return data?.kinds || {};
  } catch (error) {
    return {};
  }
};

export const generateSimilarSentencesStream = async (payload, handlers = {}) => {
  const {
    onProgress,
    onChunk,
    onSaved,
    onCancelled,
    onError,
    inactivityTimeoutMs = 90_000,
  } = handlers;
  const authHeaders = await getAuthHeaders();
  const timeoutMs = calcApiTimeout('similar-sentences', {
    studentCount: payload.count || 10,
  });
  const response = await fetchWithRetry(
    getApiUrl('/api/saenggibu/similar-sentences-stream'),
    {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify(payload),
    },
    {
      maxRetries: 1,
      timeoutMs,
      retryOnTimeout: false,
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.detail || '비슷한 문장을 만들지 못했어요.');
  }

  const events = readSseJson(response.body, {
    idleTimeoutMs: inactivityTimeoutMs,
    idleTimeoutError: () => new Error('생성 서버 응답이 잠시 멈췄어요. 다시 시도해주세요.'),
  });
  for await (const parsed of events) {
    if (parsed.type === 'progress') onProgress?.(parsed);
    else if (parsed.type === 'chunk') onChunk?.(parsed);
    else if (parsed.type === 'saved') onSaved?.(parsed);
    else if (parsed.type === 'cancelled') onCancelled?.(parsed);
    else if (parsed.type === 'error' || parsed.error) {
      onError?.(parsed);
      throw new Error(parsed.message || parsed.error || '비슷한 문장을 만들지 못했어요.');
    }
  }
};

// ========================================
// 내 스타일 (교사 스타일 프로필) API
// ========================================

// 내 스타일 전체 조회 (Free도 조회 가능)
// 가입 첫날 버전·인원 제한 해제 상태 (서버 플래그·A/B, 생성 API가 다시 판정한다)
export const getFirstDayUnlock = async () => {
  return await apiRequest('/api/saenggibu/first-day-unlock');
};

export const getStyleProfiles = async () => {
  return await apiRequest('/api/saenggibu/style-profiles');
};

// 우수 예시/지침 1회 AI 분석 → 스타일 명세 초안
export const analyzeStyleSources = async (scope, texts) => {
  return await apiRequestWithRetry('/api/saenggibu/style-profiles/analyze', {
    method: 'POST',
    body: JSON.stringify({ scope, texts }),
  }, {
    maxRetries: 1,
    timeoutMs: 180_000,
    retryOnTimeout: false,
  });
};

// 우수 예시/지침 파일 업로드 → 추출 → 1회 AI 분석
export const analyzeStyleFiles = async (scope, files, extraText = '') => {
  const user = auth.currentUser;
  const headers = {};
  if (user) {
    try {
      headers['Authorization'] = `Bearer ${await user.getIdToken()}`;
    } catch (error) {
      console.error('토큰 가져오기 실패:', error);
    }
  }
  const formData = new FormData();
  formData.append('scope', scope);
  (files || []).forEach((file) => formData.append('files', file));
  if (extraText) formData.append('extra_text', extraText);

  const response = await fetchWithRetry(
    getApiUrl('/api/saenggibu/style-profiles/analyze-files'),
    { method: 'POST', headers, body: formData },
    { maxRetries: 1, timeoutMs: 300_000, retryOnTimeout: false }
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.detail || `HTTP error! status: ${response.status}`);
  }
  return await response.json();
};

// 내 스타일 저장/수정 (PRO 전용)
export const upsertStyleProfile = async (scope, payload) => {
  return await apiRequest(`/api/saenggibu/style-profiles/${encodeURIComponent(scope)}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

// 내 스타일 전체 적용 ON/OFF (마스터 스위치, PRO 전용)
export const setStyleMasterEnabled = async (enabled) => {
  return await apiRequest('/api/saenggibu/style-profiles/master', {
    method: 'POST',
    body: JSON.stringify({ enabled }),
  });
};

// 내 스타일 삭제
export const deleteStyleProfile = async (scope) => {
  return await apiRequest(`/api/saenggibu/style-profiles/${encodeURIComponent(scope)}`, {
    method: 'DELETE',
  });
};

// 참고 예시 1개 추가 (결과 카드 1탭 저장, PRO 전용)
export const addStyleExample = async (scope, payload = {}) => {
  return await apiRequest(`/api/saenggibu/style-profiles/${encodeURIComponent(scope)}/examples`, {
    method: 'POST',
    body: JSON.stringify({
      text: payload.text,
      category: payload.category ?? null,
      source: payload.source || 'from_output',
    }),
  });
};

// 참고 예시 1개 삭제 (내 스타일 화면은 PUT 전체 교체를 쓰므로 보조 경로)
export const deleteStyleExample = async (scope, exampleId) => {
  return await apiRequest(
    `/api/saenggibu/style-profiles/${encodeURIComponent(scope)}/examples/${encodeURIComponent(exampleId)}`,
    { method: 'DELETE' },
  );
};

const compactSuggestionQuery = (params) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    search.set(key, String(value));
  });
  const query = search.toString();
  return query ? `?${query}` : '';
};

// 생성 완료 후 스마트 제안 1장 조회 (비로그인·에러는 호출측에서 fail-open)
export const getSaenggibuSuggestion = async ({
  recordId,
  category,
  isBatch = false,
  copied = false,
  edited = false,
  versionIndex,
} = {}) => {
  return await apiRequest(`/api/saenggibu/suggestions${compactSuggestionQuery({
    record_id: recordId,
    category,
    is_batch: isBatch ? '1' : undefined,
    copied: copied ? '1' : undefined,
    edited: edited ? '1' : undefined,
    version_index: versionIndex,
  })}`);
};

export const acceptSaenggibuSuggestion = async ({
  fingerprint,
  type,
  payload,
  option,
  text,
} = {}) => {
  return await apiRequest('/api/saenggibu/suggestions/accept', {
    method: 'POST',
    body: JSON.stringify({
      fingerprint,
      type,
      payload,
      ...(option != null && option !== '' ? { option } : {}),
      ...(text != null ? { text } : {}),
    }),
  });
};

export const dismissSaenggibuSuggestion = async ({ fingerprint, type } = {}) => {
  return await apiRequest('/api/saenggibu/suggestions/dismiss', {
    method: 'POST',
    body: JSON.stringify({ fingerprint, type }),
  });
};

export const getSaenggibuUsageSummary = async () => {
  return await apiRequest('/api/saenggibu/usage-summary');
};

// 진행 중인 단일 생기부 생성 작업 중단
export const cancelSaenggibuGeneration = async (jobId) => {
  return await apiRequest(`/api/saenggibu/generation-jobs/${encodeURIComponent(jobId)}/cancel`, {
    method: 'POST'
  });
};

// 생성 작업 상태 조회 (네트워크 끊김 후 저장된 결과 복구용)
export const getSaenggibuGenerationJobStatus = async (jobId) => {
  return await apiRequest(`/api/saenggibu/generation-jobs/${encodeURIComponent(jobId)}`, {
    method: 'GET'
  });
};

// ========================================
// 자료함에서 고르기 (Phase 4b)
// ========================================

// 자료함 목록 — 생기부 계획서류(plan_like) 가 먼저 온다. 실패는 호출자가 조용히 숨긴다.
export const getSaenggibuLibraryMaterials = async () => {
  return await apiRequest('/api/saenggibu/library-materials');
};

// 자료함 항목을 업로드 대신 분석한다. 응답 shape 는 /api/saenggibu/file/upload 와 같다 (+ source, material_id).
export const analyzeSaenggibuFileFromLibrary = async ({ materialId, category }, { signal, timeoutMs } = {}) => {
  return await apiRequestWithRetry('/api/saenggibu/file/from-library', {
    method: 'POST',
    body: JSON.stringify({ material_id: materialId, category }),
    ...(signal ? { signal } : {}),
  }, {
    maxRetries: 0,
    timeoutMs: timeoutMs || calcApiTimeout('saenggibu-file-analysis', { fileCount: 1, hasImages: false }),
    retryOnTimeout: false,
  });
};
